// Section 19 and 13: Raid. 5% per season x Threat level, Autumn bias; foreshadowed half the time
// (a watch post's "earlier warning"). Raiders come for food and tools. Choices:
//   Fight: guards engaged. Up to 30% of food and tools are taken; a manned palisade halves that;
//     each safety point beyond 2 reduces it 10%. Each guard: 10% injured, 3% killed. With no
//     guards the raiders may take a person (Section 13 kidnap; recorded as lost to a raid).
//   Pay them: give 20% of the food.
//   Hide: lose 40% of the exposed stock (a manned palisade halves it, E1-10), nobody hurt.
// One Section 13 rule set (M6): the losses, the guards engaged and the kidnap chance come from
// `raidOutcome` (core/queries.ts, Stream E1); this file only rolls the dice and applies them.
// The choice is answered before production, so last season's safety points count. Losses are
// recorded for the Section 13 safety need (`raidLoss`).
// Constants: config.events.raid, config.threats, config.jobs.guard, config.buildings.palisade.
import type { Config } from '../../config';
import { choiceOutcome, choiceRng, eventEntry, eventPrompt } from '../../core/content-helpers';
import {
  guardsOnDuty,
  lastCompletedReport,
  peopleSorted,
  raidOutcome,
  safetyPointsOf,
  type RaidChoice,
  type RaidOutcome,
} from '../../core/queries';
import { defineEvent, noModifiers, type EventChoiceDef } from '../../core/registry';
import type { Delta, ReadContext } from '../../core/types';

/** Section 13 losses for a choice, with the safety points of the last completed season. */
function outcome(ctx: ReadContext, choice: RaidChoice): RaidOutcome {
  return raidOutcome(
    ctx.state,
    ctx.cfg,
    choice,
    safetyPointsOf(lastCompletedReport(ctx.state)),
  );
}

const round1 = (x: number): number => Math.round(x * 10) / 10;

export function raid(config: Config) {
  const c = config.events.raid;
  const pct = (x: number): number => Math.round(x * 100);
  const choices: EventChoiceDef[] = [
    {
      id: 'fight',
      label: 'Fight',
      preview: (ctx) => {
        const out = outcome(ctx, 'fight');
        return [
          { key: 'raid-fight', params: { loss: pct(out.lossFraction), guards: out.guardIds.length } },
        ];
      },
      apply: (ctx, prompt) => {
        const out = outcome(ctx, 'fight');
        const food = ctx.state.stocks.food.amount * out.foodLoss;
        const tools = ctx.state.stocks.tools.amount * out.toolLoss;
        const rng = choiceRng(prompt, 'fight');
        const deltas: Delta[] = [
          { kind: 'stock', resource: 'food', amount: -food, flow: 'theft' },
          { kind: 'stock', resource: 'tools', amount: -tools, flow: 'theft' },
          { kind: 'raidLoss', fraction: out.lossFraction, source: 'event:raid' },
        ];
        const hurt: string[] = [];
        const dead: string[] = [];
        for (const id of out.guardIds) {
          const p = ctx.state.people[id];
          if (p === undefined) continue;
          const injury = rng.float();
          const death = rng.float();
          if (death < out.guardDeath) {
            dead.push(p.name);
            deltas.push({ kind: 'kill', personId: p.id, cause: 'raid', source: 'event:raid' });
          } else if (injury < out.guardInjury) {
            hurt.push(p.name);
            deltas.push({ kind: 'injure', personId: p.id, source: 'event:raid' });
          }
        }
        let taken = '';
        const everyone = peopleSorted(ctx.state);
        if (out.kidnap > 0 && everyone.length > 0 && rng.float() < out.kidnap) {
          const p = rng.pick(everyone);
          taken = p.name;
          deltas.push({ kind: 'kill', personId: p.id, cause: 'raid', source: 'event:raid' });
        }
        deltas.push(
          choiceOutcome(prompt, 'fight', {
            food: round1(food),
            tools: round1(tools),
            hurt: hurt.join(', '),
            dead: dead.join(', '),
            taken,
          }),
        );
        return deltas;
      },
    },
    {
      id: 'pay',
      label: 'Pay them',
      preview: (ctx) => [
        {
          key: 'raid-pay',
          params: { food: round1(ctx.state.stocks.food.amount * outcome(ctx, 'pay').foodLoss) },
        },
      ],
      apply: (ctx, prompt) => {
        const out = outcome(ctx, 'pay');
        const food = ctx.state.stocks.food.amount * out.foodLoss;
        return [
          { kind: 'stock', resource: 'food', amount: -food, flow: 'other' },
          { kind: 'raidLoss', fraction: out.lossFraction, source: 'event:raid' },
          choiceOutcome(prompt, 'pay', { food: round1(food) }),
        ];
      },
    },
    {
      id: 'hide',
      label: 'Hide',
      preview: (ctx) => [{ key: 'raid-hide', params: { loss: pct(outcome(ctx, 'hide').foodLoss) } }],
      apply: (ctx, prompt) => {
        const out = outcome(ctx, 'hide');
        const food = ctx.state.stocks.food.amount * out.foodLoss;
        const tools = ctx.state.stocks.tools.amount * out.toolLoss;
        return [
          { kind: 'stock', resource: 'food', amount: -food, flow: 'theft' },
          { kind: 'stock', resource: 'tools', amount: -tools, flow: 'theft' },
          { kind: 'raidLoss', fraction: out.lossFraction, source: 'event:raid' },
          choiceOutcome(prompt, 'hide', { food: round1(food), tools: round1(tools) }),
        ];
      },
    },
  ];
  return defineEvent({
    id: 'raid',
    label: 'Raid',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['autumn'],
    foreshadow: { chance: config.eventDeck.foreshadowShare },
    ledgerTag: 'raid',
    weight: (ctx) =>
      ctx.state.stocks.food.amount + ctx.state.stocks.tools.amount > 0 ? c.baseChance : 0,
    text: (ctx) => eventEntry('raid', { guards: guardsOnDuty(ctx.state, ctx.cfg).length }),
    // M7: the prompt carries the headline's number of guards (it read " guards stand ready").
    apply: (ctx) => [
      eventPrompt(ctx, 'raid', choices, {
        defaultChoiceId: 'pay',
        params: { guards: guardsOnDuty(ctx.state, ctx.cfg).length },
      }),
    ],
    choices,
    modifierTargets: [],
    modifiers: noModifiers,
  });
}
